import type { GameEvent } from '../../core/events';

/**
 * Map a sim event to the audio key to play, or null for "no sound".
 * Pure — no Phaser. `projectileFired` for cheese is null (cheese is an aura
 * tower and never fires a projectile, but we guard defensively).
 */
export function eventToSfxKey(e: GameEvent): string | null {
  switch (e.type) {
    case 'towerPlaced':
      return 'sfx-place';
    case 'towerSold':
      return 'sfx-sell';
    case 'towerUpgraded':
      return 'sfx-upgrade';
    case 'towerStunned':
      return 'sfx-stun';
    case 'towerSlowed':
      // The Brocco's Veggie Hex reuses the tower-debuff sting (no new audio).
      return 'sfx-stun';
    case 'towerPoisoned':
      // The Poison Shroom's dose: the same debuff sting; each tick is a towerHit.
      return 'sfx-stun';
    case 'enemyDodged':
      // The Mushy's dodge is the floating "DODGE"; the miss itself is silent.
      return null;
    case 'towerDodged':
      // The Shroom's dodge: the floating "DODGE" over the tower, silent too.
      return null;
    case 'enemyShroomnitized':
      // The Snowshroom's mark is the debuff sting the towers get.
      return 'sfx-stun';
    case 'enemyStruckEnemy':
      // The strike lands with the bomb's boom, like a fireball on a tower.
      return 'sfx-boom';
    case 'placementLocked':
      // Emperor Pepro's Imperial Decree reuses the same debuff sting.
      return 'sfx-stun';
    case 'fireballLaunched':
      // King Ember's fireball reuses the bomb's launch whump — no new audio.
      return 'sfx-fire-bomb';
    case 'towerHit':
      // …and lands with the bomb's boom.
      return 'sfx-boom';
    case 'towerDestroyed':
      // A tower burning off its plot reuses the sell sting: the same "this
      // tower is leaving" cue, without a new sound to generate.
      return 'sfx-sell';
    case 'projectileFired':
      // Cheese and Wise are auras (no projectile). The bomb DOES have a launch
      // sound (sfx-fire-bomb) and still booms on impact (see projectileImpact).
      if (e.towerType === 'cheese' || e.towerType === 'wise') return null;
      // Reuse existing fire SFX for the shop/gift towers so no new audio is
      // needed: the Ninja's katana uses the Bladesman's metallic slash, and
      // Sensei's ketchup uses the Saucemaster's wet-blob shot.
      if (e.towerType === 'ninja') return 'sfx-fire-blade';
      // Spear Hambi's thrust is a blade strike too.
      if (e.towerType === 'spear') return 'sfx-fire-blade';
      if (e.towerType === 'sensei') return 'sfx-fire-sauce';
      // Shrekbook Hambi fires the Hambi Tower's shot — same projectile, so the
      // same report.
      if (e.towerType === 'shrekbook') return 'sfx-fire-patty';
      // The Capybara Event's three (spec 2026-09-16) reuse existing reports
      // rather than adding audio: Capy's bite is a blade strike, Galactic
      // Capy throws the Fire Mage's fireball, and General Capy's runner sets
      // off from the base exactly as Lightning Hambi's cloud does.
      if (e.towerType === 'capy') return 'sfx-fire-blade';
      if (e.towerType === 'galacticcapy') return 'sfx-fire-firemage';
      if (e.towerType === 'generalcapy') return 'sfx-fire-lightning';
      // Update 1.2's four (spec 2026-09-17) do the same: Hog Hambi bites like
      // the Capy, Mage Capy's beam cracks like the Fire Mage's fireball, Mini
      // Gunner fires the Gunner Hambi's bullet — the same shot, so the same
      // report — and King Capy's gems arrive with Lightning Hambi's snap.
      if (e.towerType === 'hog') return 'sfx-fire-blade';
      if (e.towerType === 'magecapy') return 'sfx-fire-firemage';
      if (e.towerType === 'minigunner') return 'sfx-fire-gunner';
      if (e.towerType === 'kingcapy') return 'sfx-fire-lightning';
      // King Hambi's yellow shards snap like King Capy's gems.
      if (e.towerType === 'kinghambi') return 'sfx-fire-lightning';
      // The Golden Takeout Bag's blades (spec 2026-09-26): Dagger Hambi's stab
      // and Samurai Slicer's slash AND thrown daggers are blade strikes.
      // Leafstorm Hambi's tornado has its own whoosh, sfx-fire-leafstorm.
      if (e.towerType === 'dagger' || e.towerType === 'samurai') return 'sfx-fire-blade';
      // Its three more (spec 2026-09-29): the Mayo Masher's sludge is a wet
      // blob like the Saucemaster's, and Ultra Hammy (and his minions) throw
      // with the BBQ Bomber's whump. The Grease Goddess is an aura, silent.
      if (e.towerType === 'grease') return null;
      if (e.towerType === 'masher') return 'sfx-fire-sauce';
      if (e.towerType === 'ultrahammy') return 'sfx-fire-bomb';
      // The Dark Shop (spec 2026-10-12) reuses existing stings.
      if (e.towerType === 'horror') return 'sfx-fire-sauce';
      if (e.towerType === 'demonic') return 'sfx-fire-bomb';
      if (e.towerType === 'hamreaper') return 'sfx-fire-firemage';
      // The Light Shop (spec 2026-10-13): only Angelic Hambi ever fires.
      if (e.towerType === 'angelic') return 'sfx-fire-firemage';
      if (e.towerType === 'healer' || e.towerType === 'banker') return null;
      // The Golden Shroom throws with the Angelic's firemage whoosh.
      if (e.towerType === 'goldenshroom') return 'sfx-fire-firemage';
      return `sfx-fire-${e.towerType}`;
    case 'projectileImpact':
      // The Aqua Sorcerer's water ball bursts with a wet splash where it lands —
      // the impact sound IS the tower's signature, so it gets its own key rather
      // than reusing the bomb's boom.
      if (e.towerType === 'aquasorcerer') return 'sfx-splash-aquasorcerer';
      // Ultra Hammy's dynamite booms like the bomb (the sim only reports a
      // dynamite impact — never his tier-1 fireball or tier-4 crystal).
      // Demonic Hambi's skulls blow up like the bomb too.
      return e.towerType === 'bomb' || e.towerType === 'ultrahammy' || e.towerType === 'demonic'
        ? 'sfx-boom'
        : null;
    case 'critHit':
      // Purely visual (the CRITICAL/HEADSHOT/BLUE FLAME bubble). The tower's own
      // fire sound already played when the shot went out, and a second sting at
      // this cadence would just muddy it.
      return null;
    case 'shardRainCast':
      // King Hambi's K–O ceremony is silent theatre; the landing booms.
      return null;
    case 'shardRainLanded':
      return 'sfx-boom';
    case 'endThemCast':
      // Ultra Hammy's END THEM is silent theatre too; its dynamite booms.
      return null;
    case 'endThemLanded':
      return 'sfx-boom';
    case 'endThemEnded':
      return null;
    case 'scytheCast':
      // The Ham Reaper's scythe whooshes in with the blade sting.
      return 'sfx-fire-blade';
    case 'scytheEnded':
      return null;
    case 'shroomFrenzyCast':
      // The Golden Shroom's cutscene is silent theatre, like the K–O; it booms.
      return null;
    case 'shroomFrenzyLanded':
      return 'sfx-boom';
    case 'towerHypnotized':
      // A Hyno-Fungi's hypnosis: the tower-debuff sting (the stun or possession
      // it causes already stings, so this one stays quiet).
      return null;
    case 'bossSummoned':
      return null;
    case 'towerHealed':
    case 'bankerPaid':
      // Healer Hambi's sparkles and the Banker's "+$" are visual; every kill
      // already has its own sting.
      return null;
    case 'specialFired':
      // The phantom's red-bullet cinematic is its own spectacle; no sting.
      return null;
    case 'enemyKilled':
      return `sfx-die-${e.enemyType}`;
    case 'enemyLeaked':
      // The life counter dropping is its own cue; no sting.
      return null;
    case 'chipDropped':
      // Chips land silently — the sprite appearing is the signal.
      return null;
    case 'chipPressed':
      // A press reuses the placement click.
      return 'sfx-place';
    case 'chipExpired':
      // K.O reuses the tower-debuff sting.
      return 'sfx-stun';
    case 'bossTransformed':
      // The second form lands with the bomb's boom — the screen is shaking
      // anyway, and no new audio is needed.
      return 'sfx-boom';
    case 'towerPossessed':
      // A ghost takes a tower: the tower-debuff sting, like a stun.
      return 'sfx-stun';
    case 'bossArrived':
      // The Shadow Reaper lands with the second form's boom.
      return 'sfx-boom';
    case 'enemySplit':
      // The Halolight's own kill sting already plays; the split is visual.
      return null;
    case 'laserFired':
      // Prince Nacho's laser sweep reuses the Laser Nacho's report, which is
      // the Ember Barrage launch whump.
      return 'sfx-fire-bomb';
    case 'shieldDropped':
      // A shield lands like a press target does.
      return 'sfx-place';
    case 'shieldHeld':
      // Held in time: the wave-clear chime, scaled down to one beat.
      return 'sfx-wave-clear';
    case 'shieldMissed':
      // Two lives gone — the same debuff sting K.O uses.
      return 'sfx-stun';
    case 'waveStarted':
      return 'sfx-wave-start';
    case 'waveCleared':
      return 'sfx-wave-clear';
    case 'won':
      return 'sfx-victory';
    case 'lost':
      return 'sfx-defeat';
  }
}

/**
 * Some events play a randomly-chosen voice line INSTEAD of their base SFX from
 * `eventToSfxKey` — a wave-start battle cry, or an episode-loss taunt. This
 * returns the KEY PREFIX of that event's variant pool (or null for none). The
 * concrete keys (e.g. `wave-start-fry-them`, `losing-you-should-be-ashamed`) are
 * discovered at play time from whatever audio files were bundled, so new lines
 * drop in by adding a file — no code change. Prefixes are chosen to never match
 * a base SFX key (`sfx-wave-start`, `sfx-defeat` both start with `sfx-`), so the
 * base is only used as a fallback when no voice clips are bundled.
 */
export function voiceLinePrefix(e: GameEvent): string | null {
  switch (e.type) {
    case 'waveStarted':
      return 'wave-start-';
    case 'lost':
      return 'losing-';
    default:
      return null;
  }
}
